/**
 * Selectors for guests slice.
 * Compute derived state on-read instead of storing + syncing via effects.
 * All selectors are pure functions of (members, membership, ...).
 */

/**
 * Find the active guest object by ID.
 * Falls back to first guest if ID not found.
 */
export const selectActiveGuest = (members, activeGuestId) => {
  if (!members || !Array.isArray(members)) return null;
  return members.find((m) => m.id === activeGuestId) || members[0] || null;
};

/**
 * Flatten all services from all members.
 * Used for cart display and service count.
 */
export const selectSelectedServices = (members) => {
  if (!members || !Array.isArray(members)) return [];
  return members.flatMap((m) => m.services || []);
};

/**
 * Count total services across all members.
 */
export const selectServiceCount = (members) => {
  if (!members || !Array.isArray(members)) return 0;
  return members.reduce((sum, m) => sum + (m.services?.length || 0), 0);
};

/**
 * Compute cart total from all services + membership pricing.
 * Pure function; no state or storage needed.
 */
export const selectCartTotal = (members, membership) => {
  if (!members || !Array.isArray(members)) return 0;
  const flat = selectSelectedServices(members);
  return flat.reduce((sum, service) => {
    const price = parseFloat(
      membership
        ? service?.price_rate?.member_after_6_or_weekends
        : service?.price_rate?.nonMemberPrice
    ) || 0;
    return sum + price;
  }, 0);
};

/**
 * Count total number of guests/members.
 */
export const selectMemberCount = (members) => {
  return members && Array.isArray(members) ? members.length : 0;
};

/**
 * Service count for the active guest only.
 */
export const selectActiveGuestServiceCount = (members, activeGuestId) => {
  const guest = selectActiveGuest(members, activeGuestId);
  return guest?.services?.length || 0;
};

/**
 * Subtotal for services of the active guest (using membership pricing).
 */
export const selectActiveGuestSubtotal = (members, activeGuestId, membership) => {
  const guest = selectActiveGuest(members, activeGuestId);
  if (!guest) return 0;
  const svcs = guest.services || [];
  return svcs.reduce((sum, service) => {
    const price = parseFloat(
      membership
        ? service?.price_rate?.member_after_6_or_weekends
        : service?.price_rate?.nonMemberPrice
    ) || 0;
    return sum + price;
  }, 0);
};

/**
 * Check if user can proceed to next step in group booking.
 * For group: every guest must have at least 1 service.
 * For single: primary guest must have at least 1 service.
 * For other: false.
 */
export const selectGroupNextEnabled = (
  members,
  isSingleBooking,
  isGroupBooking
) => {
  if (!members || !Array.isArray(members)) return false;

  if (isGroupBooking) {
    if (members.length === 0) return false;
    return members.every((m) => (m?.services?.length ?? 0) > 0);
  }

  if (isSingleBooking) {
    return (members[0]?.services?.length ?? 0) > 0;
  }

  return false;
};

/**
 * Extract service IDs from all selected services.
 * Used for API submissions and comparisons.
 */
export const selectSelectedServicesID = (members) => {
  return selectSelectedServices(members).map((s) => s.id);
};

/**
 * Find the index of the active guest in the members array.
 */
export const selectSelectedMemberIndex = (members, activeGuestId) => {
  if (!members || !Array.isArray(members)) return 0;
  const idx = members.findIndex((m) => m.id === activeGuestId);
  return idx >= 0 ? idx : 0;
};

/**
 * Get max total service duration across all guests' services.
 * Requires: getMaxTotalDuration from Utils/Helper.
 * This is imported from existing code; left to caller to provide.
 */
export const selectOccupiedTime = (members, getMaxTotalDuration) => {
  if (!members || !getMaxTotalDuration) return 0;
  return getMaxTotalDuration(members);
};
